import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { ResponsiveCombobox, type ComboboxItem } from '@/components/ui/responsive-combobox';
import { useOrganizations } from '@/features/organizations/hooks/useOrganizationManagement';
import { useUpdateAdminUser } from '../../hooks/useUserManagement';
import {
  editAdminUserSchema,
  type EditUserFormValues,
} from '../../schemas/userSchemas';
import { roleLabels } from '../UserTable/UserTable.columns';
import { type AdminUser } from '@/types/user';

interface EditUserFormProps {
  user: AdminUser;
  onClose: () => void;
  formId?: string;
}

export function EditUserForm({ user, onClose, formId = 'edit-form' }: EditUserFormProps) {
  const updateUserMutation = useUpdateAdminUser();
  
  // Fetch organizations dynamically
  const { data: orgData, isLoading: isLoadingOrgs } = useOrganizations();
  
  const organizations: ComboboxItem[] = (orgData?.organizations || []).map(org => ({
    value: org.id.toString(),
    label: org.name,
    venueCount: org.venue_count,
  }));

  const form = useForm<EditUserFormValues>({
    resolver: zodResolver(editAdminUserSchema),
    defaultValues: {
      name: user.full_name || '',
      email: user.email || '',
      phone: user.phone_number || '',
      role: Array.isArray(user.role) ? user.role[0] : user.role,
      organization_id: user.organization ? parseInt(user.organization.id) : null,
      is_active: user.is_active,
    },
  });

  const handleSubmit = async (values: EditUserFormValues) => {
    try {
      await updateUserMutation.mutateAsync({
        userId: user.id,
        data: values,
      });
      onClose();
    } catch (error) {
      // Error is handled by the mutation hook
    }
  };

  return (
    <Form {...form}>
      <form
        id={formId}
        className="space-y-4"
        onSubmit={form.handleSubmit(handleSubmit)}
        data-testid="edit-user-form"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="John Doe"
                  data-testid="edit-user-form-name"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email Address *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="john@example.com"
                  data-testid="edit-user-form-email"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone Number *</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  data-testid="edit-user-form-phone"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger data-testid="edit-user-form-role-select">
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.entries(roleLabels).map(([value, label]) => (
                    <SelectItem
                      key={value}
                      value={value}
                      data-testid={`edit-user-form-role-option-${value}`}
                    >
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organization (optional)</FormLabel>
              <FormControl>
                <ResponsiveCombobox
                  items={organizations}
                  value={field.value ? field.value.toString() : undefined}
                  onValueChange={(value) => field.onChange(value ? parseInt(value) : null)}
                  placeholder={isLoadingOrgs ? "Loading organizations..." : "Select organization"}
                  searchPlaceholder="Search organizations..."
                  disabled={isLoadingOrgs}
                  data-testid="edit-user-form-org-select"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Active Status</FormLabel>
                <div className="text-sm text-muted-foreground">
                  {field.value ? 'User can access the system' : 'User is deactivated'}
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="edit-user-form-status-switch"
                />
              </FormControl>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}